/* ==========================================================================
   collection.js — the collection page without page reloads
   --------------------------------------------------------------------------
   Every filter control on the page belongs to one form (#CollectionFacets),
   wherever it is drawn: the side sheet, a quick popover in the tool row, the
   in-stock switch, the sort menu (sections/t-collection.liquid). A change
   sends the form's state to the Section Rendering API and the regions marked
   data-swap are replaced from the answer, so the counts, chips, the category
   row and the grid update in place, and the address keeps the state for
   sharing and for the back button. Also here: the sheet and the popovers,
   grid density, "show more" and adding a size to the cart from a card. The
   model's colours on a card live in card-colors.js, for every page.
   Without this file the form submits and the page reloads with the result.
   ========================================================================== */
if (!customElements.get('collection-page')) customElements.define('collection-page', class extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;
    this.form = document.getElementById('CollectionFacets');
    this.sheet = this.querySelector('#CollectionSheet');
    this.timer = 0;
    this.request = null;

    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('input', (e) => this.onInput(e));
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('keydown', (e) => this.onKey(e));
    document.addEventListener('click', (e) => { if (!e.target.closest('.cquick')) this.closeQuick(); });
    window.addEventListener('popstate', () => this.render(location.search.slice(1), false));

    this.applyCols();
    this.watchMore();
    this.showSort();
    this.pinNamed(location.search.slice(1));
  }

  /* ---- what the form says ------------------------------------------------ */
  query() {
    const params = new URLSearchParams();
    if (!this.form) {
      const sort = this.querySelector('[data-sort]');
      if (sort) params.set('sort_by', sort.value);
      return params.toString();
    }
    const seen = new Set();
    for (const el of this.form.elements) {
      if (!el.name || el.disabled) continue;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;
      let value = String(el.value).trim();
      if (el.dataset.price) {
        /* An open end is no restriction: an empty "from", or "to" at the top */
        const n = parseInt(value, 10);
        if (!value || Number.isNaN(n)) continue;
        if (el.dataset.price === 'lo' && n <= 0) continue;
        if (el.dataset.price === 'hi' && n >= Number(el.max)) continue;
        value = String(n);
      }
      const pair = `${el.name}=${value}`;
      if (seen.has(pair)) continue;   // the same control is drawn twice (sheet and popover)
      seen.add(pair);
      params.append(el.name, value);
    }
    return params.toString();
  }

  /* Copies of one control (in the sheet and in a popover) move together */
  mirror(source) {
    if (!this.form) return;
    for (const el of this.form.elements) {
      if (el === source || el.name !== source.name) continue;
      if (el.type === 'checkbox' && el.value === source.value) el.checked = source.checked;
      if (el.dataset.price && el.dataset.price === source.dataset.price) el.value = source.value;
    }
  }

  /* The search page marks hidden fields data-keep (the query and its options):
     whatever link or state a request is built from, they travel with it */
  keep(params) {
    this.form?.querySelectorAll('input[data-keep]').forEach((el) => { if (!params.has(el.name)) params.set(el.name, el.value); });
    return params;
  }

  submit(delay = 0) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.render(this.query(), true), delay);
  }

  async render(query, push) {
    this.request?.abort();
    const request = (this.request = new AbortController());
    query = this.keep(new URLSearchParams(query)).toString();
    /* Absolute, so a <base> in the document cannot send it elsewhere */
    const here = location.origin + location.pathname;
    const page = query ? `${here}?${query}` : here;
    this.classList.add('is-loading');
    try {
      const res = await fetch(`${here}?section_id=${this.section}${query ? `&${query}` : ''}`, { signal: request.signal });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      this.swap(doc);
      this.pinNamed(query);
      if (push) history.pushState({ collection: true }, '', page);
    } catch (err) {
      if (err.name !== 'AbortError') location.href = page;
    } finally {
      if (this.request === request) this.classList.remove('is-loading');
    }
  }

  /* Replace every data-swap region, keeping what the visitor was doing:
     folded groups stay folded, the sheet keeps its scroll, a brand search
     keeps its text, and focus returns to the same control */
  swap(doc) {
    const folded = new Set([...this.querySelectorAll('details.cfacet:not([open])')].map((d) => d.dataset.facet));
    const body = this.querySelector('.csheet__body');
    const scroll = body ? body.scrollTop : 0;
    const finds = [...this.querySelectorAll('[data-find]')].filter((f) => f.value).map((f) => [f.id, f.value]);
    const focusId = this.contains(document.activeElement) ? document.activeElement.id : '';

    doc.querySelectorAll('[data-swap]').forEach((fresh) => {
      const old = this.querySelector(`[data-swap="${fresh.dataset.swap}"]`);
      if (old) old.innerHTML = fresh.innerHTML;
    });

    /* The sort selects sit outside the swapped regions: follow the answer
       (it differs from the control after the back button) */
    const sorted = doc.querySelector('[data-sort]');
    if (sorted) this.querySelectorAll('[data-sort], [data-sort-mirror]').forEach((s) => { s.value = sorted.value; });
    this.showSort();

    this.querySelectorAll('details.cfacet').forEach((d) => { if (folded.has(d.dataset.facet)) d.open = false; });
    if (body) body.scrollTop = scroll;
    finds.forEach(([id, value]) => { const f = document.getElementById(id); if (f) { f.value = value; this.find(f); } });
    if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    this.applyCols();
    this.watchMore();
  }

  /* ---- search: what the query names comes first -------------------------- */
  /* Here sold-out products follow everything in stock (unavailable_products=
     last), so a sold-out product searched by its full name sits pages away.
     On the first page in the relevance order the query is asked once more
     with sold-out products in their place (show), and the products the query
     names (every word of it starts a word of the name) lead the page: in
     stock first, then sold out. "Show more" skips them later. A query that
     names a whole page in stock (a brand) is left as it is. */
  static nameWords(text) {
    return text.toLowerCase().normalize('NFKD').replace(/\p{M}/gu, '').split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  }

  static namedBy(query, name) {
    const asked = this.nameWords(query);
    const words = this.nameWords(name || '');
    return asked.length > 0 && asked.every((word) => words.some((w) => w.startsWith(word)));
  }

  async pinNamed(query) {
    if (!this.classList.contains('cpage--search')) return;
    const turn = (this.pinTurn = (this.pinTurn || 0) + 1);
    const params = new URLSearchParams(query);
    const terms = params.get('q') || '';
    if (!terms.trim() || (params.get('sort_by') || 'relevance') !== 'relevance' || Number(params.get('page') || 1) > 1) return;

    const named = (cell) => this.constructor.namedBy(terms, cell.dataset.name);
    const sold = (cell) => cell.getAttribute('prod-instock') !== 'true';
    const grid = this.querySelector('[data-swap="grid"]');
    const cells = grid ? [...grid.querySelectorAll(':scope > x-cell[data-product]')] : [];
    const namedStock = cells.filter((cell) => !sold(cell) && named(cell));
    if (!cells.length || (namedStock.length === cells.length && this.querySelector('a[data-next]'))) return;

    params.set('options[unavailable_products]', 'show');
    params.delete('page');
    params.set('section_id', this.section);
    let doc;
    try {
      const res = await fetch(`${location.origin}${location.pathname}?${params}`);
      if (!res.ok) return;
      doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    } catch (err) {
      return;
    }
    if (turn !== this.pinTurn) return;

    /* Sold-out ones in the relevance order; those already on the page move up */
    const here = new Map(cells.map((cell) => [cell.dataset.product, cell]));
    const namedSold = [];
    doc.querySelectorAll('[data-swap="grid"] > x-cell[data-product]').forEach((cell) => {
      if (!sold(cell) || !named(cell)) return;
      const mine = here.get(cell.dataset.product);
      if (!mine) cell.classList.add('is-added');
      namedSold.push(mine || cell);
    });
    cells.forEach((cell) => { if (sold(cell) && named(cell) && !namedSold.includes(cell)) namedSold.push(cell); });
    if (!namedSold.length) return;
    grid.prepend(...namedStock, ...namedSold);
  }

  /* ---- events ------------------------------------------------------------ */
  onChange(e) {
    const t = e.target;
    if (t.matches('[data-sort-mirror]')) {
      const sort = this.querySelector('[data-sort]');
      if (sort) sort.value = t.value;
      this.showSort();
      this.submit();
      return;
    }
    if (t.matches('[data-find], [data-range-lo], [data-range-hi]')) return;
    if (t.form !== this.form && !t.matches('[data-sort]')) return;
    if (t.matches('[data-sort]')) {
      const mirror = this.querySelector('[data-sort-mirror]');
      if (mirror) mirror.value = t.value;
    }
    this.mirror(t);
    this.submit(t.dataset.price ? 350 : 0);
  }

  onInput(e) {
    const t = e.target;
    if (t.matches('[data-find]')) this.find(t);
    if (t.matches('[data-range-lo], [data-range-hi]')) this.slide(t);
    if (t.dataset.price) { this.mirror(t); this.submit(800); }
  }

  onClick(e) {
    const t = e.target;
    const link = t.closest('a[data-facet-link]');
    if (link) { e.preventDefault(); this.render(new URL(link.href, location.href).search.slice(1), true); return; }
    const next = t.closest('a[data-next]');
    if (next) { e.preventDefault(); this.more(next); return; }
    if (t.closest('[data-open-filters]')) { this.openSheet(); return; }
    if (t.closest('[data-close-filters]')) { this.closeSheet(); return; }
    const quick = t.closest('[data-quick]');
    if (quick) { this.toggleQuick(quick, e.detail === 0); return; }   /* detail 0: pressed from the keyboard */
    const pick = t.closest('[data-sort-pick]');
    if (pick) { this.pickSort(pick); return; }
    const preset = t.closest('[data-preset]');
    if (preset) { this.preset(preset); return; }
    const cols = t.closest('[data-cols]');
    if (cols) { this.setCols(Number(cols.dataset.cols)); return; }
    const add = t.closest('[data-qs-add]');
    if (add && !add.disabled) { e.preventDefault(); this.add(add); }
  }

  onKey(e) {
    if (e.key === 'Escape') { this.closeQuick(true); this.closeSheet(); return; }
    /* Arrows walk the sort menu */
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && e.target.matches('[data-sort-pick]')) {
      e.preventDefault();
      const items = [...e.target.parentElement.querySelectorAll('[data-sort-pick]')];
      items[(items.indexOf(e.target) + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus();
      return;
    }
    /* Keep Tab inside the open sheet */
    if (e.key === 'Tab' && this.sheet && this.sheet.classList.contains('is-open')) {
      const items = [...this.sheet.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary')].filter((el) => el.offsetParent !== null && !el.closest('[hidden]'));
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* ---- brand search ------------------------------------------------------ */
  find(input) {
    const q = input.value.trim().toLowerCase();
    const list = input.parentElement.querySelector('.copts');
    if (!list) return;
    let letter = null, any = false;
    const close = () => { if (letter) letter.hidden = !any; };
    [...list.children].forEach((li) => {
      if (li.classList.contains('copts__letter')) { close(); letter = li; any = false; return; }
      const show = !q || (li.dataset.findItem || '').includes(q);
      li.hidden = !show;
      if (show) any = true;
    });
    close();
  }

  /* ---- price ------------------------------------------------------------- */
  slide(t) {
    const body = t.closest('.cfacet__body');
    const range = body.querySelector('[data-range]');
    const lo = body.querySelector('[data-range-lo]'), hi = body.querySelector('[data-range-hi]');
    const max = Number(range.dataset.max);
    let a = Number(lo.value), b = Number(hi.value);
    if (a > b - 100) { if (t === lo) a = b - 100; else b = a + 100; lo.value = a; hi.value = b; }
    const fill = range.querySelector('.crange__fill');
    fill.style.setProperty('--lo', `${(a / max) * 100}%`);
    fill.style.setProperty('--hi', `${(b / max) * 100}%`);
    const from = body.querySelector('[data-price="lo"]'), to = body.querySelector('[data-price="hi"]');
    from.value = a > 0 ? a : '';
    to.value = b < max ? b : '';
    this.mirror(from); this.mirror(to);
    this.submit(450);
  }

  preset(btn) {
    const body = btn.closest('.cfacet__body');
    const [a, b] = btn.dataset.preset.split('-').map(Number);
    const from = body.querySelector('[data-price="lo"]'), to = body.querySelector('[data-price="hi"]');
    from.value = a > 0 ? a : '';
    to.value = b < Number(to.max) ? b : '';
    this.mirror(from); this.mirror(to);
    this.submit();
  }

  /* ---- sheet and popovers ------------------------------------------------ */
  openSheet() {
    if (!this.sheet) return;
    this.closeQuick();
    this.lastFocus = document.activeElement;
    this.sheet.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.sheet.classList.add('is-open')));
    document.documentElement.classList.add('csheet-lock');
    this.querySelectorAll('[data-open-filters]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    setTimeout(() => this.sheet.querySelector('.csheet__close')?.focus(), 60);
  }

  closeSheet() {
    if (!this.sheet || this.sheet.hidden) return;
    this.sheet.classList.remove('is-open');
    document.documentElement.classList.remove('csheet-lock');
    this.querySelectorAll('[data-open-filters]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => { if (!this.sheet.classList.contains('is-open')) this.sheet.hidden = true; }, 520);
    if (this.lastFocus && this.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    /* Back to the top of the results if they start above the screen */
    const tools = this.querySelector('.ctools');
    if (tools && tools.getBoundingClientRect().top < 0) tools.scrollIntoView({ block: 'start' });
  }

  toggleQuick(btn, keyboard = false) {
    const box = btn.closest('.cquick');
    const open = !box.classList.contains('is-open');
    this.closeQuick();
    if (open) {
      box.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      if (keyboard) box.querySelector('[data-sort-pick][aria-checked="true"]')?.focus({ preventScroll: true });
    }
  }

  /* refocus: hand focus back to the button of a popover closed from the keyboard */
  closeQuick(refocus = false) {
    this.querySelectorAll('.cquick.is-open').forEach((box) => {
      box.classList.remove('is-open');
      const btn = box.querySelector('[data-quick]');
      btn?.setAttribute('aria-expanded', 'false');
      if (refocus && box.contains(document.activeElement)) btn?.focus({ preventScroll: true });
    });
  }

  /* ---- sort -------------------------------------------------------------- */
  pickSort(item) {
    const sort = this.querySelector('[data-sort]');
    this.closeQuick(true);
    if (!sort || sort.value === item.dataset.sortPick) return;
    sort.value = item.dataset.sortPick;
    const mirror = this.querySelector('[data-sort-mirror]');
    if (mirror) mirror.value = sort.value;
    this.showSort();
    this.submit();
  }

  /* The menu says what the select holds: its button's word and the mark */
  showSort() {
    const sort = this.querySelector('[data-sort]');
    if (!sort) return;
    const label = this.querySelector('[data-sort-label]');
    if (label && sort.selectedOptions[0]) label.textContent = sort.selectedOptions[0].textContent.trim();
    this.querySelectorAll('[data-sort-pick]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.sortPick === sort.value)));
  }

  /* ---- density ----------------------------------------------------------- */
  setCols(n) {
    try { localStorage.setItem('sstories-cols', String(n)); } catch (e) { /* private mode */ }
    this.applyCols();
  }

  applyCols() {
    const grid = this.querySelector('.cgrid');
    if (!grid) return;
    let n = Number(grid.getAttribute('columns')) || 4;
    try { n = Number(localStorage.getItem('sstories-cols')) || n; } catch (e) { /* private mode */ }
    grid.style.setProperty('--cols', n);
    grid.dataset.cols = n;
    this.querySelectorAll('[data-cols]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.cols) === n)));
  }

  /* ---- show more --------------------------------------------------------- */
  async more(link) {
    if (link.classList.contains('is-loading')) return;
    link.classList.add('is-loading');
    try {
      const url = new URL(link.href, location.href);
      this.keep(url.searchParams);
      url.searchParams.set('section_id', this.section);
      const doc = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
      const grid = this.querySelector('[data-swap="grid"]');
      /* A card already shown (moved to the first page by pinNamed) is not repeated */
      const shown = new Set([...grid.querySelectorAll(':scope > x-cell[data-product]')].map((cell) => cell.dataset.product));
      doc.querySelectorAll('[data-swap="grid"] > x-cell:not(.cedit):not(.cempty)').forEach((cell) => {
        if (shown.has(cell.dataset.product)) return;
        cell.classList.add('is-added');
        grid.append(cell);
      });
      const fresh = doc.querySelector('[data-swap="more"]');
      const old = this.querySelector('[data-swap="more"]');
      if (fresh && old) old.innerHTML = fresh.innerHTML;
      this.watchMore();
    } catch (err) {
      location.href = link.href;
    }
  }

  /* "Load on scroll": the button fetches itself when it comes near */
  watchMore() {
    if (this.dataset.more !== 'scroll' || !('IntersectionObserver' in window)) return;
    this.moreObserver?.disconnect();
    const link = this.querySelector('a[data-next]');
    if (!link) return;
    this.moreObserver = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) this.more(link); }, { rootMargin: '600px 0px' });
    this.moreObserver.observe(link);
  }

  /* ---- a size from the card into the cart -------------------------------- */
  add(btn) {
    const id = Number(btn.dataset.qsAdd);
    btn.classList.add('is-loading');
    const done = () => btn.classList.remove('is-loading');
    const opened = (count) => {
      done();
      const counter = document.getElementById('counter');
      if (counter && count != null) counter.setAttribute('data-count', count);
      document.body.classList.add('open-cart');
    };
    if (window.CartJS && window.jQuery) {
      /* The theme's cart drawer re-renders itself from CartJS */
      window.jQuery(document).one('cart.requestComplete', (event, cart) => opened(cart && cart.item_count));
      window.CartJS.addItem(id, 1, {}, { error: (xhr) => { done(); this.say(xhr && xhr.responseJSON && xhr.responseJSON.description); } });
      return;
    }
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) })
      .then((r) => (r.ok ? (location.href = '/cart') : r.json().then((j) => { done(); this.say(j.description); })))
      .catch(() => done());
  }

  say(text) {
    let toast = this.querySelector('.ctoast');
    if (!toast) { toast = document.createElement('div'); toast.className = 'ctoast'; toast.setAttribute('role', 'status'); this.append(toast); }
    toast.textContent = text || 'Не вдалося додати. Спробуйте ще раз.';
    toast.classList.add('is-on');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => toast.classList.remove('is-on'), 3200);
  }
});
